import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, CheckCircle2, AlertCircle, Sparkles, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { toast } from "sonner";

const services = [
  "Webinár pre tím",
  "Prezenčný workshop",
  "AI riešenie na mieru",
  "Potrebujem odporúčanie",
];

const contactSchema = z.object({
  name: z.string().trim().min(2, "Meno musí mať aspoň 2 znaky").max(100),
  email: z.string().trim().email("Zadajte pracovný e-mail v správnom formáte.").max(255),
  organization: z.string().trim().max(200).optional(),
  service: z.string().min(1, "Vyberte, o čo máte záujem"),
  message: z.string().trim().min(10, "Správa musí mať aspoň 10 znakov").max(2000),
  consent: z.boolean().refine(val => val === true, "Pre pokračovanie je potrebný súhlas.")
});

type ContactFormData = z.infer<typeof contactSchema>;

interface ContactFormSectionProps {
  prefilledService?: string;
}

export function ContactFormSection({ prefilledService }: ContactFormSectionProps) {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    organization: "",
    service: "",
    message: "",
    consent: false
  });
  const [honeypot, setHoneypot] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [submitError, setSubmitError] = useState("");

  // AI helper state
  const [showAiHelper, setShowAiHelper] = useState(false);
  const [aiContext, setAiContext] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  // Pre-fill service/message when user selects a service on the homepage
  useEffect(() => {
    if (prefilledService) {
      const matched = services.find(s =>
        s.toLowerCase() === prefilledService.toLowerCase() ||
        prefilledService.toLowerCase().includes(s.toLowerCase())
      );
      setFormData(prev => ({
        ...prev,
        service: matched || prev.service,
        message: matched
          ? `Mám záujem o ${matched.toLowerCase()}.

`
          : prev.message
      }));
    }
  }, [prefilledService]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));

    // Clear error on change
    if (errors[name as keyof ContactFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleAiGenerate = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-message-helper", {
        body: {
          name: formData.name || undefined,
          context: aiContext || undefined
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.message) {
        setFormData(prev => ({ ...prev, message: data.message }));
        setShowAiHelper(false);
        setAiContext("");
        toast.success("Správa vygenerovaná!");
      }
    } catch (error) {
      console.error("AI helper error:", error);
      toast.error("Nepodarilo sa vygenerovať správu. Skúste to znova.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    setErrors({});

    // Honeypot check
    if (honeypot) {
      console.log("Bot detected");
      return;
    }

    // Validate
    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ContactFormData, string>> = {};
      result.error.errors.forEach(err => {
        const field = err.path[0] as keyof ContactFormData;
        fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      const firstField = ["name", "email", "organization", "service", "message", "consent"].find(f => fieldErrors[f as keyof ContactFormData]);
      if (firstField) {
        const el = document.querySelector<HTMLElement>(`[name="${firstField}"], #${firstField}`);
        el?.focus();
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Get UTM params from URL
      const urlParams = new URLSearchParams(window.location.search);

      // Call secure edge function instead of direct database access
      const { data, error } = await supabase.functions.invoke("submit-contact", {
        body: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          organization: formData.organization?.trim() || null,
          service: formData.service.trim(),
          message: formData.message.trim(),
          consent: formData.consent,
          source_url: window.location.href,
          utm_source: urlParams.get("utm_source") || null,
          utm_medium: urlParams.get("utm_medium") || null,
          utm_campaign: urlParams.get("utm_campaign") || null
        }
      });

      if (error) {
        throw error;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setIsSuccess(true);
      setFormData({ name: "", email: "", organization: "", service: "", message: "", consent: false });
    } catch (error: unknown) {
      console.error("Contact form error:", error);
      const errorMessage = error instanceof Error ? error.message : "Niečo sa pokazilo.";
      if (errorMessage.includes("Too many requests")) {
        setSubmitError("Príliš veľa pokusov. Skúste to prosím neskôr.");
      } else {
        setSubmitError("Niečo sa pokazilo. Skúste to, prosím, neskôr.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card rounded-2xl p-8 md:p-12 border border-border shadow-lg text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          aria-hidden="true"
        >
          <CheckCircle2 className="w-10 h-10 text-green-700" />
        </motion.div>
        <h3 className="font-heading text-2xl md:text-3xl text-charcoal mb-4" tabIndex={-1}>
          Ďakujem. Váš dopyt bol odoslaný.
        </h3>
        <p className="text-lg text-muted-foreground">
          Ozvem sa vám do 24 hodín.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      noValidate
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeInUp}
      className="bg-card rounded-2xl p-6 md:p-10 border border-border shadow-lg"
    >
      {submitError && (
        <div role="alert" className="mb-6 p-4 bg-destructive/10 border border-destructive/30 rounded-xl flex items-center gap-3 text-destructive">
          <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          <span>{submitError}</span>
        </div>
      )}

      {/* Honeypot field - hidden from users */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="absolute opacity-0 pointer-events-none"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className="space-y-5">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-charcoal mb-2">
            Meno a priezvisko <span className="text-muted-foreground font-normal">(povinné)</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={isSubmitting}
            required
            aria-required="true"
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={errors.name ? "name-error" : undefined}
            className={`w-full px-4 py-3 rounded-xl border bg-background text-foreground transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus:ring-2 focus:ring-gold/60 ${
              errors.name ? "border-destructive" : "border-border hover:border-gold/50"
            }`}
            placeholder="Vaše meno"
          />
          {errors.name && (
            <p id="name-error" role="alert" className="mt-2 text-sm text-destructive">{errors.name}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-charcoal mb-2">
            Pracovný e-mail <span className="text-muted-foreground font-normal">(povinné)</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={isSubmitting}
            required
            aria-required="true"
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={errors.email ? "email-error" : undefined}
            className={`w-full px-4 py-3 rounded-xl border bg-background text-foreground transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus:ring-2 focus:ring-gold/60 ${
              errors.email ? "border-destructive" : "border-border hover:border-gold/50"
            }`}
            placeholder="vas@email.sk"
          />
          {errors.email && (
            <p id="email-error" role="alert" className="mt-2 text-sm text-destructive">{errors.email}</p>
          )}
        </div>

        {/* Organization */}
        <div>
          <label htmlFor="organization" className="block text-sm font-medium text-charcoal mb-2">
            Organizácia <span className="text-muted-foreground font-normal">(nepovinné)</span>
          </label>
          <input
            type="text"
            id="organization"
            name="organization"
            value={formData.organization}
            onChange={handleChange}
            disabled={isSubmitting}
            aria-invalid={errors.organization ? true : undefined}
            aria-describedby={errors.organization ? "organization-error" : undefined}
            className={`w-full px-4 py-3 rounded-xl border bg-background text-foreground transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus:ring-2 focus:ring-gold/60 ${
              errors.organization ? "border-destructive" : "border-border hover:border-gold/50"
            }`}
            placeholder="Názov školy, úradu alebo firmy"
          />
          {errors.organization && (
            <p id="organization-error" role="alert" className="mt-2 text-sm text-destructive">{errors.organization}</p>
          )}
        </div>

        {/* Service */}
        <div>
          <label htmlFor="service" className="block text-sm font-medium text-charcoal mb-2">
            Mám záujem o <span className="text-muted-foreground font-normal">(povinné)</span>
          </label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            disabled={isSubmitting}
            required
            aria-required="true"
            aria-invalid={errors.service ? true : undefined}
            aria-describedby={errors.service ? "service-error" : undefined}
            className={`w-full px-4 py-3 rounded-xl border bg-background text-foreground transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus:ring-2 focus:ring-gold/60 appearance-none ${
              errors.service ? "border-destructive" : "border-border hover:border-gold/50"
            }`}
          >
            <option value="" disabled>Vyberte možnosť</option>
            {services.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {errors.service && (
            <p id="service-error" role="alert" className="mt-2 text-sm text-destructive">{errors.service}</p>
          )}
        </div>

        {/* Message */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <label htmlFor="message" className="block text-sm font-medium text-charcoal">
              S čím potrebujete pomôcť? <span className="text-muted-foreground font-normal">(povinné)</span>
            </label>
            <button
              type="button"
              onClick={() => setShowAiHelper(true)}
              disabled={isSubmitting || isGenerating}
              aria-expanded={showAiHelper}
              className="inline-flex items-center gap-1.5 min-h-[44px] px-3 py-1.5 text-sm text-gold-dark hover:text-gold-dark bg-gold/10 hover:bg-gold/20 rounded-full transition-all duration-300 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
            >
              <Sparkles className="w-4 h-4" aria-hidden="true" />
              Pomôž mi formulovať
            </button>
          </div>

          {/* AI Helper Panel */}
          <AnimatePresence>
            {showAiHelper && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 overflow-hidden"
              >
                <div className="p-4 bg-gold/5 border border-gold/20 rounded-xl">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 text-gold-dark">
                      <Sparkles className="w-4 h-4" aria-hidden="true" />
                      <span className="text-sm font-medium">AI Asistent</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAiHelper(false)}
                      aria-label="Zavrieť AI asistenta"
                      className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-muted-foreground hover:text-charcoal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 rounded-full"
                    >
                      <X className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                  <label htmlFor="ai-context" className="block text-sm text-muted-foreground mb-3">
                    O čom by ste sa chceli dozvedieť? (nepovinné)
                  </label>
                  <input
                    type="text"
                    id="ai-context"
                    value={aiContext}
                    onChange={(e) => setAiContext(e.target.value)}
                    disabled={isGenerating}
                    placeholder="napr. AI školenie pre učiteľov, automatizácia..."
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus:ring-2 focus:ring-gold/60 mb-3"
                  />
                  <button
                    type="button"
                    onClick={handleAiGenerate}
                    disabled={isGenerating}
                    className="w-full bg-gold text-charcoal py-2 px-4 min-h-[44px] rounded-lg font-medium text-sm flex items-center justify-center gap-2 hover:bg-gold-light transition-all duration-300 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-charcoal focus-visible:ring-offset-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                        Generujem...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" aria-hidden="true" />
                        Vygenerovať správu
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            disabled={isSubmitting}
            required
            aria-required="true"
            aria-invalid={errors.message ? true : undefined}
            aria-describedby={errors.message ? "message-error" : undefined}
            rows={5}
            className={`w-full px-4 py-3 rounded-xl border bg-background text-foreground transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus:ring-2 focus:ring-gold/60 resize-none ${
              errors.message ? "border-destructive" : "border-border hover:border-gold/50"
            }`}
            placeholder="Stručne opíšte svoju situáciu alebo pracovnú úlohu..."
          />
          {errors.message && (
            <p id="message-error" role="alert" className="mt-2 text-sm text-destructive">{errors.message}</p>
          )}
        </div>

        {/* GDPR Consent */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              name="consent"
              checked={formData.consent}
              onChange={handleChange}
              disabled={isSubmitting}
              required
              aria-required="true"
              aria-invalid={errors.consent ? true : undefined}
              aria-describedby={errors.consent ? "consent-error" : undefined}
              className="mt-1 w-5 h-5 rounded border-border text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus:ring-gold/60"
            />
            <span className="text-sm text-foreground/70 group-hover:text-charcoal transition-colors">
              Súhlasím so spracovaním mojich údajov podľa{" "}
              <a
                href="/gdpr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold-dark hover:text-charcoal underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 rounded-sm"
              >
                GDPR
              </a>
              . <span className="text-muted-foreground">(povinné)</span>
            </span>
          </label>
          {errors.consent && (
            <p id="consent-error" role="alert" className="mt-2 text-sm text-destructive">{errors.consent}</p>
          )}
        </div>


        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full bg-charcoal text-primary-foreground py-4 px-6 min-h-[52px] rounded-xl font-medium text-lg flex items-center justify-center gap-3 hover:bg-charcoal/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
              Odosielam...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" aria-hidden="true" />
              Odoslať nezáväzný dopyt
            </>
          )}
        </motion.button>
        <p className="text-center text-sm text-muted-foreground">
          Odpovedám osobne do 24 hodín.
        </p>
      </div>
    </motion.form>
  );
}

export default ContactFormSection;
